/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { find } from 'lodash'

import type { StackDeployment, DeploymentStatus } from '@modules/ui-types'

import {
  isEsStopping,
  isEsStopped,
  hasOngoingConfigurationChange,
} from '@/lib/stackDeployments/selectors/configurationChanges'

import { getHighestSeverity, getDeploymentHealthProblems } from '../healthProblems'

export function getDeploymentStatus({
  deployment,
}: {
  deployment: StackDeployment
}): DeploymentStatus {
  const { healthy } = deployment
  const [problems] = getDeploymentHealthProblems({ deployment, eolStatus: null })
  const severity = getHighestSeverity(problems)

  if (severity === `warning`) {
    return { healthy, status: 'warning' }
  }

  if (isEsStopping({ deployment })) {
    return { healthy, status: 'stopping' }
  }

  if (isPending(problems, hasOngoingConfigurationChange({ deployment }))) {
    return { healthy, status: 'pending' }
  }

  if (isEsStopped({ deployment })) {
    return { healthy, status: 'stopped' }
  }

  if (isUnhealthy(problems, severity)) {
    return { healthy, status: 'unhealthy' }
  }

  if (severity === `info`) {
    return { healthy, status: 'healthy' }
  }

  return { healthy, status: healthy ? 'healthy' : 'unhealthy' }
}

export function isPending(problems, pending) {
  return pending || find(problems, { id: `deployment-being-created` })
}

function isUnhealthy(problems, severity) {
  return find(problems, { id: `failed-initial-plan` }) || severity === `danger`
}
